import styles from './TextInput.module.scss';
import {
  type ReactNode,
  type FocusEvent,
  type KeyboardEvent,
  Ref,
  useRef,
  ChangeEvent,
  useContext,
  useEffect,
} from 'react';
import { useMergeRefs } from '@libs/react/ref';
import { FormContext } from '../form/FormContext';
import { FormFieldContext } from '../form/FormFieldContext';
import { Input } from './Input';
import { InputContent } from './InputContent';
import { InputAddon } from './InputAddon';
import clsx from 'clsx';
import { FormFieldInputContext } from '../form/FormField';
import { InputStatus } from './InputStatus';

type TextInputType = 'text' | 'number' | 'email' | 'password';
type Align = 'start' | 'center' | 'end';

type SharedProps = {
  align?: Align;
  defaultValue?: string | number;
  disabled?: boolean;
  id?: string;
  maxLength?: number;
  name?: string;
  onValue?: (value: string) => void;
  required?: boolean;
  value?: string;
  status?: InputStatus;
};

type InputProps = {
  lines?: undefined;
  max?: number;
  min?: number;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  prefix?: ReactNode;
  preventNewline?: undefined;
  ref?: Ref<HTMLInputElement>;
  step?: number;
  suffix?: string;
  type?: TextInputType | undefined;
};

type TextareaProps = {
  lines: number;
  max?: undefined;
  min?: undefined;
  onBlur?: (e: FocusEvent<HTMLTextAreaElement>) => void;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onFocus?: (e: FocusEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  prefix?: undefined;
  preventNewline?: boolean;
  ref?: Ref<HTMLTextAreaElement>;
  step?: undefined;
  suffix?: undefined;
  type: 'textarea';
};

type Message = {
  content: string;
  type: 'error' | 'info';
};

type BaseProps = {
  addon?: ReactNode;
  bottom?: ReactNode;
  grow?: boolean | number;
  label: string;
  message?: Message;
  start?: ReactNode;
  valid?: boolean;
};

type Props = BaseProps & SharedProps & (InputProps | TextareaProps);

export function TextInput({ ref, addon, label, ...props }: Props) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const mergedRef = useMergeRefs<HTMLInputElement | HTMLTextAreaElement>(
    ref,
    inputRef,
  );

  const onClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const start = props.start ? <InputAddon>{props.start}</InputAddon> : null;
  const end = addon ? <InputAddon>{addon}</InputAddon> : null;

  return (
    <Input label={label} onClick={onClick}>
      {start}
      <InputContent align={props.align}>
        {({ id }) => {
          return props.type === 'textarea' ? (
            <Textarea {...props} ref={mergedRef} id={props.id ?? id} />
          ) : (
            <Textinput {...props} ref={mergedRef} id={props.id ?? id} />
          );
        }}
      </InputContent>
      {end}
    </Input>
  );
}

function Textinput(props: SharedProps & InputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputMergedRef = useMergeRefs(inputRef, props.ref);
  const formContext = useContext(FormContext);
  const formFieldContext = useContext(FormFieldContext);
  const formFieldInputContext = useContext(FormFieldInputContext);
  const name = props.name ?? formFieldContext.name;

  const className = clsx(
    styles['text-input-element'],
    styles[`align-${props.align}`],
  );

  const prefix = props.prefix && (
    <span className={styles['text-input-prefix']}>{props.prefix}</span>
  );
  const suffix = (
    <span className={styles['text-input-prefix']}>{props.suffix}</span>
  );

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    props.onChange?.(event);
    props.onValue?.(value);
  };

  useEffect(() => {
    const input = inputRef.current;
    if (name && input) {
      const prev = formContext.inputs.get(name) ?? [];
      formContext.inputs.set(name, [...prev, input]);
      if (formFieldInputContext) {
        formFieldInputContext.set(name, input);
      }
      return () => {
        formContext.inputs.delete(name);
      };
    }
  }, [formContext, name, formFieldInputContext]);

  return (
    <>
      {prefix}
      <input
        ref={inputMergedRef}
        className={className}
        defaultValue={props.defaultValue}
        disabled={props.disabled}
        id={props.id}
        max={props.max}
        maxLength={props.maxLength}
        min={props.min}
        name={props.name}
        onBlur={props.onBlur}
        onChange={onChange}
        onFocus={props.onFocus}
        onKeyDown={props.onKeyDown}
        required={props.required}
        step={props.step}
        type={props.type}
      />
      {suffix}
    </>
  );
}

function Textarea(props: SharedProps & TextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mergedRef = useMergeRefs(textareaRef, props.ref);
  const formContext = useContext(FormContext);
  const formFieldContext = useContext(FormFieldContext);
  const formFieldInputContext = useContext(FormFieldInputContext);
  const name = props.name ?? formFieldContext.name;

  const className = clsx(
    styles['text-input-element'],
    styles[`align-${props.align}`],
  );

  const onChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.currentTarget.value;
    props.onChange?.(event);
    props.onValue?.(value);
  };

  function onKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (props.preventNewline && event.key === 'Enter') {
      event.preventDefault();
    }
    props.onKeyDown?.(event);
  }

  useEffect(() => {
    if (name && textareaRef.current) {
      const prev = formContext.inputs.get(name) ?? [];
      formContext.inputs.set(name, [...prev, textareaRef.current]);
      formFieldInputContext?.set(name, textareaRef.current);
      return () => {
        formContext.inputs.delete(name);
      };
    }
  }, [formContext, name, formFieldInputContext]);

  return (
    <textarea
      ref={mergedRef}
      className={className}
      defaultValue={props.defaultValue}
      disabled={props.disabled}
      maxLength={props.maxLength}
      name={props.name}
      onBlur={props.onBlur}
      onChange={onChange}
      onFocus={props.onFocus}
      onKeyDown={onKeyDown}
      required={props.required}
    />
  );
}
